const MAX_RESOLUTION_HOPS = 10
const IGNORED_PREFIXES = ['/api', '/uploads', '/_nuxt']

export const pathOf = url => {
  const path = (url || '').split('#')[0].split('?')[0]

  return (path.length > 1 ? path.replace(/\/+$/, '') : path) || '/'
}

export const isIgnoredPath = url => {
  const path = pathOf(url)

  return IGNORED_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`)
  )
}

const queryOf = url => {
  const [withoutHash] = (url || '').split('#')
  const queryIndex = withoutHash.indexOf('?')

  return queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1)
}

export const appendQuery = (target, originalUrl) => {
  const originalQuery = queryOf(originalUrl)

  if (!originalQuery) return target

  const [withoutHash, ...hashParts] = (target || '').split('#')
  const hash = hashParts.length ? `#${hashParts.join('#')}` : ''
  const [base, ...queryParts] = withoutHash.split('?')
  const merged = new URLSearchParams(queryParts.join('?'))
  const targetKeys = new Set(merged.keys())

  new URLSearchParams(originalQuery).forEach((value, key) => {
    if (!targetKeys.has(key)) merged.append(key, value)
  })

  const query = merged.toString()

  return `${base}${query ? `?${query}` : ''}${hash}`
}

const compileRule = ({ old_url, new_url }, index) => {
  const hasWildcard = old_url.includes('*')
  const normalized = old_url.length > 1 ? old_url.replace(/\/+$/, '') : old_url
  const escaped = normalized.replace(/[.+?^${}()|[\]\\]/g, '\\$&')
  const pattern = escaped.replace(/\\\?\*/g, '(?:\\?.*)?').replace(/\*/g, '.*')

  return {
    index,
    from: old_url,
    to: new_url,
    statusCode: 301,
    needsScan: hasWildcard,
    regex: new RegExp(
      hasWildcard ? `^${pattern}/?$` : `^${pattern}/?(?:[?#]|$)`
    ),
  }
}

const indexRules = rules => {
  const rulesByPath = new Map()
  const scannedRules = []

  rules.forEach(rule => {
    if (rule.needsScan) {
      scannedRules.push(rule)

      return
    }

    const path = pathOf(rule.from)

    if (!rulesByPath.has(path)) {
      rulesByPath.set(path, rule)
    }
  })

  return url => {
    if (isIgnoredPath(url)) return null

    const indexed = rulesByPath.get(pathOf(url))

    return scannedRules.reduce(
      (winner, rule) =>
        rule.regex.test(url) && (!winner || rule.index < winner.index)
          ? rule
          : winner,
      indexed && indexed.regex.test(url) ? indexed : null
    )
  }
}

const resolveFinalTarget = (rule, match) => {
  const visited = new Set([pathOf(rule.from)])
  let target = rule.to

  for (let hop = 0; hop < MAX_RESOLUTION_HOPS; hop += 1) {
    const next = match(target)

    if (!next) return target
    if (visited.has(pathOf(next.to))) return null

    visited.add(pathOf(target))
    target = next.to
  }

  return target
}

const buildRedirectionMatcher = rows => {
  const compiled = rows.map(compileRule)
  const selfRedirecting = new Set(
    compiled.filter(rule => rule.regex.test(rule.to))
  )
  const linkable = compiled.filter(rule => !selfRedirecting.has(rule))
  const matchLinkable = indexRules(linkable)

  const resolved = []
  const cyclic = []
  let flattened = 0

  linkable.forEach(rule => {
    const target = resolveFinalTarget(rule, matchLinkable)

    if (target === null) {
      cyclic.push(rule)

      return
    }

    if (target !== rule.to) flattened += 1

    resolved.push({ ...rule, to: target })
  })

  return {
    match: indexRules(resolved),
    stats: {
      received: rows.length,
      applied: resolved.length,
      selfRedirecting: [...selfRedirecting].map(rule => rule.from),
      cyclic: cyclic.map(rule => rule.from),
      flattened,
    },
  }
}

export default buildRedirectionMatcher
